import { useEffect, useState } from "react"
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router"
import { auth } from "@/lib/auth"
import { Button, Input, Spinner, Toast } from "@/components/ui"
import { useT } from "@/lib/i18n"
import { LegalLinks } from "@/components/LegalLinks"

interface SignupSearch {
  invite?: string
  /** A friend's referral code (`lucy-xxxxxx`) — goes in the same field. */
  ref?: string
}

export const Route = createFileRoute("/signup")({
  validateSearch: (s: Record<string, unknown>): SignupSearch => ({
    invite: typeof s.invite === "string" ? s.invite : undefined,
    ref: typeof s.ref === "string" ? s.ref : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (context.auth.isLoggedIn()) {
      throw redirect({ to: "/chat" })
    }
  },
  component: SignupPage,
})

type SignupMode = "open" | "invite" | "unknown"

function SignupPage() {
  const t = useT()
  const nav = useNavigate()
  const search = Route.useSearch()
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [password, setPassword] = useState("")
  const [invite, setInvite] = useState(search.invite ?? search.ref ?? "")
  const [mode, setMode] = useState<SignupMode>("unknown")
  // Who sent the ?ref= link, from the public lookup. "invalid" when the
  // server doesn't know the code — the field is then cleared so the
  // visitor isn't stuck with a dead code.
  const [referrer, setReferrer] = useState<{ display_name: string; credit_usd: number } | "invalid" | null>(null)

  useEffect(() => {
    const ref = search.ref?.trim()
    if (!ref) return
    let alive = true
    fetch(`/api/public/referral/${encodeURIComponent(ref)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { display_name?: string; credit_usd?: number } | null) => {
        if (!alive) return
        if (j && typeof j.display_name === "string") {
          setReferrer({ display_name: j.display_name, credit_usd: Number(j.credit_usd ?? 0) })
        } else {
          setReferrer("invalid")
          setInvite((v) => (v.trim().toLowerCase() === ref.toLowerCase() ? "" : v))
        }
      })
      .catch(() => alive && setReferrer(null))
    return () => {
      alive = false
    }
  }, [search.ref])
  const [agree, setAgree] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Ask the server whether registration is invite-only. Until it
  // answers (or if it fails) we still render the code field so a user
  // arriving via an invite link is never stuck.
  useEffect(() => {
    let alive = true
    fetch("/api/auth/config")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { signup_mode?: string } | null) => {
        if (!alive) return
        setMode(j?.signup_mode === "open" ? "open" : "invite")
      })
      .catch(() => alive && setMode("invite"))
    return () => {
      alive = false
    }
  }, [])

  const inviteRequired = mode !== "open"

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (password.length < 12) {
      setError(t("auth.pwTooShort"))
      return
    }
    if (inviteRequired && !invite.trim()) {
      setError(t("auth.inviteRequired"))
      return
    }
    if (!agree) {
      setError(t("auth.mustAccept"))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await auth.signup(email, password, name || undefined, invite || undefined, true)
      await nav({ to: "/welcome" })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-background">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-2xl border border-border bg-card/90 p-7 shadow-glass backdrop-blur space-y-4"
      >
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t("auth.signupTitle")}</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {inviteRequired ? t("auth.inviteOnly") : t("auth.freeToStart")}
          </p>
          {referrer && referrer !== "invalid" && (
            <p data-testid="signup-referrer" className="mt-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-xs">
              🎁 {t("ref.invitedBy", { name: referrer.display_name, amount: referrer.credit_usd.toFixed(0) })}
            </p>
          )}
          {referrer === "invalid" && (
            <p className="mt-2 text-xs text-destructive">{t("ref.invalidLink")}</p>
          )}
        </div>
        {inviteRequired && (
          <div className="space-y-2">
            <label
              htmlFor="signup-invite"
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              {t("auth.inviteCode")}
            </label>
            <Input
              id="signup-invite"
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              required
              placeholder="lucy-XXXX-XXXX"
              value={invite}
              onChange={(e) => setInvite(e.target.value)}
            />
          </div>
        )}
        <div className="space-y-2">
          <label
            htmlFor="signup-email"
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            {t("auth.email")}
          </label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label
            htmlFor="signup-name"
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            {t("auth.displayName")} <span className="normal-case font-normal">{t("auth.optional")}</span>
          </label>
          <Input
            id="signup-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label
            htmlFor="signup-password"
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            {t("auth.password")}
          </label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-[10px] text-muted-foreground">
            {t("auth.min12")}
          </p>
        </div>
        <label className="flex items-start gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            required
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-0.5 accent-[hsl(var(--primary))]"
          />
          <span>
            {t("auth.acceptTerms").split(/(\{terms\}|\{privacy\})/).map((part, i) =>
              part === "{terms}" ? <Link key={i} to="/terms" target="_blank" className="underline hover:text-foreground">{t("auth.terms")}</Link>
              : part === "{privacy}" ? <Link key={i} to="/privacy" target="_blank" className="underline hover:text-foreground">{t("auth.privacy")}</Link>
              : <span key={i}>{part}</span>,
            )}
          </span>
        </label>
        {error && <Toast variant="destructive">{error}</Toast>}
        <Button type="submit" disabled={busy || !agree} className="w-full">
          {busy ? <Spinner /> : null}
          {t("auth.createAccount")}
        </Button>
        <p className="text-xs text-center text-muted-foreground">
          {t("auth.haveAccount")} <Link to="/login" className="hover:text-foreground">{t("auth.signin")}</Link>
          <span aria-hidden> · </span>
          <Link to="/pricing" className="hover:text-foreground">{t("pricing.title")}</Link>
        </p>
        <LegalLinks />
      </form>
    </div>
  )
}
